import { useQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { useCallback, useMemo } from 'react';
import {
  OrderDirectionEnum,
  type PharmacyOrderV2,
  type StringFilter,
} from 'generated-schema-types';
import PharmaciesQuery from './pharmacies.query.graphql';
import type { Pharmacy } from './types';

interface PharmacyEdge {
  cursor: string;
  node: Pharmacy;
}

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

interface UserPharmaciesData {
  pharmaciesV2: {
    edges: PharmacyEdge[];
    pageInfo: PageInfo;
  };
}

export type PharmacySortField = 'npi' | 'name' | 'nabp';

export interface PharmacySortDescriptor {
  field: PharmacySortField;
  direction: OrderDirectionEnum;
}

export interface UsePharmaciesOptions {
  npi?: StringFilter;
  zip?: StringFilter;
  pageSize?: number;
  sort?: PharmacySortDescriptor;
}

export const usePharmacies = (options?: UsePharmaciesOptions) => {
  const pageSize = options?.pageSize ?? 10;

  // Build the order object for the GraphQL query
  const order: PharmacyOrderV2 = useMemo(
    () =>
      options?.sort
        ? { [options.sort.field]: options.sort.direction }
        : { name: OrderDirectionEnum.Asc },
    [options?.sort]
  );

  const { data, loading, error, refetch } = useQuery<UserPharmaciesData>(
    PharmaciesQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        npi: options?.npi,
        zip: options?.zip,
        first: pageSize,
        after: null,
        last: null,
        before: null,
        order,
      },
    }
  );

  if (error) {
    datadogLogs.logger.error(
      'Failed to fetch user pharmacies',
      {},
      error as Error
    );
  }

  const pharmacies = data?.pharmaciesV2?.edges?.map(edge => edge.node) ?? [];
  const pageInfo = data?.pharmaciesV2?.pageInfo;

  const next = useCallback(async () => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) {
      return;
    }

    await refetch({
      npi: options?.npi,
      zip: options?.zip,
      first: pageSize,
      after: pageInfo.endCursor,
      last: null,
      before: null,
      order,
    });
  }, [refetch, pageInfo, pageSize, options?.npi, options?.zip, order]);

  const previous = useCallback(async () => {
    if (!pageInfo?.hasPreviousPage || !pageInfo?.startCursor) {
      return;
    }

    await refetch({
      npi: options?.npi,
      zip: options?.zip,
      first: null,
      after: null,
      last: pageSize,
      before: pageInfo.startCursor,
      order,
    });
  }, [refetch, pageInfo, pageSize, options?.npi, options?.zip, order]);

  return {
    pharmacies,
    isLoading: loading,
    error,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    hasPreviousPage: pageInfo?.hasPreviousPage ?? false,
    next,
    previous,
  };
};
